import { TypeIcon } from "../common/TypeIcon";
import { useMemo, useState } from "react";
import { routeAirportIds } from "../../utils/flightRoute";
import { Link, useNavigate } from "react-router-dom";
import type { TripWithId } from "../../hooks/useTrips";
import type { SegmentWithId } from "../../hooks/useSegments";
import { useAuth } from "../../contexts/AuthContext";
import { useInteractions } from "../../hooks/useInteractions";
import { useSegments, createSegment } from "../../hooks/useSegments";
import { useEntityMap } from "../../hooks/useEntityMap";
import { ActivityList } from "../itinerary/ActivityList";
import { LogActivitySheet } from "../interactions/LogActivitySheet";
import { LogActivityPicker } from "../interactions/LogActivityPicker";
import { NearbyEntities } from "../interactions/NearbyEntities";
import { SegmentForm, type SegmentFormResult } from "../segments/SegmentForm";
import { AddDropdown } from "../trips/AddDropdown";
import {
  findCurrentSegmentId,
  lastLoggedSegmentId,
  formatSegmentDateRange,
  activityDayKey,
  activityEnd,
  segmentDayLookup,
  segmentZoneLookup,
  isActivityActive,
  segmentLogSeeds,
  segmentDayCount,
  tripStatusLabel,
} from "../../utils/tripDisplay";
import {
  buildTripContextSearch,
  createInteractionAt,
  findDefaultSegmentId,
  isContainerEntityType,
  startHotelVisit,
} from "../../utils/activityLogging";
import { useHotelStays } from "../../hooks/useHotelStays";
import { stayHotel } from "../../utils/hotelStays";
import { BackAtHotelCard } from "../stays/BackAtHotelCard";
import { findDefaultParentInteractionId } from "../../utils/activityTree";
import { dayKeyOf, daysBetweenKeys, todayKey } from "../../utils/calendarDay";
import { getInteractionEntityType, isCruiseDay, type EntityType } from "../../types";
import { LiveNowCard } from "./LiveNowCard";
import { TodaySoFar } from "./TodaySoFar";
import { QuickStepRow } from "./QuickStepRow";
import { upNextActivities } from "../../utils/upNext";
import "./CurrentTripCard.css";
import "./LiveNowCard.css";

interface CurrentTripCardProps {
  trip: TripWithId;
  focused: boolean;
  showFocusToggle: boolean;
  onFocus: () => void;
}

export function CurrentTripCard({ trip, focused, showFocusToggle, onFocus }: CurrentTripCardProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { interactions, loading: interactionsLoading } = useInteractions(trip.id);
  const { segments } = useSegments(trip.id);
  const [logOpen, setLogOpen] = useState(false);
  const [segmentOpen, setSegmentOpen] = useState(false);
  const [startingNearby, setStartingNearby] = useState(false);

  const entityIds = useMemo(() => {
    const fromInteractions = interactions
      .map((i) => i.entityId)
      .filter((id): id is string => !!id);
    const fromSegments = segments.map((s) => s.entityId).filter((id): id is string => !!id);
    return [...new Set([...fromInteractions, ...fromSegments, ...routeAirportIds(interactions)])];
  }, [interactions, segments]);
  const { entityMap, entityDataMap } = useEntityMap(entityIds);

  const status = tripStatusLabel(trip.startDate, trip.endDate);
  const currentSegment = useMemo(() => {
    const id = findCurrentSegmentId(segments, lastLoggedSegmentId(interactions));
    return id ? segments.find((s) => s.id === id) ?? null : null;
  }, [segments, interactions]);

  const activeInteractions = useMemo(
    () =>
      interactions.filter((i) =>
        isActivityActive(
          i.events,
          getInteractionEntityType(
            i.entityId ? { type: entityDataMap[i.entityId]?.type ?? undefined } : null,
            i.customResponses,
          ),
          i.customResponses,
        ),
      ),
    [interactions, entityDataMap],
  );
  const onDay = useMemo(() => segmentDayLookup(segments), [segments]);

  const typeOf = (i: (typeof interactions)[number]) =>
    getInteractionEntityType(
      i.entityId ? { type: entityDataMap[i.entityId]?.type ?? undefined } : null,
      i.customResponses,
    );
  // The one thing you're doing now: the latest open activity that isn't a
  // place you're merely inside (a park, a hotel). Those read as its context.
  // With nothing else open, the place itself is what you're doing (waiting at
  // the airport), so it takes the card rather than a row below.
  const liveActivity = useMemo(() => {
    const latest = (list: typeof activeInteractions) =>
      [...list].sort(
        (a, b) => (activityEnd(b, onDay)?.getTime() ?? 0) - (activityEnd(a, onDay)?.getTime() ?? 0),
      )[0] ?? null;
    const leaves = activeInteractions.filter((i) => !isContainerEntityType(typeOf(i)));
    return latest(leaves) ?? latest(activeInteractions);
    // typeOf reads entityDataMap, which is listed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeInteractions, entityDataMap, onDay]);
  // Everything else still open, places first: the airport you're still in
  // while the Uber you just ordered takes the big card, so "Left" stays a tap
  // away instead of somewhere down the trip.
  const alsoOpen = useMemo(() => {
    if (!liveActivity) return [];
    const rest = activeInteractions.filter((i) => i.id !== liveActivity.id);
    const byRecent = (a: (typeof rest)[number], b: (typeof rest)[number]) =>
      (activityEnd(b, onDay)?.getTime() ?? 0) - (activityEnd(a, onDay)?.getTime() ?? 0);
    const places = rest.filter((i) => isContainerEntityType(typeOf(i))).sort(byRecent);
    const things = rest.filter((i) => !isContainerEntityType(typeOf(i))).sort(byRecent);
    return [...places, ...things];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeInteractions, liveActivity, entityDataMap, onDay]);
  // Set up ahead with no step logged yet (the airport started from the plane):
  // waiting for its first tap, which it gets right here.
  const readyToStart = useMemo(() => upNextActivities(interactions), [interactions]);
  const shownAbove = useMemo(
    () => [...activeInteractions, ...readyToStart],
    [activeInteractions, readyToStart],
  );
  const liveParentName = useMemo(() => {
    if (!liveActivity) return null;
    const parent = liveActivity.parentInteractionId
      ? interactions.find((i) => i.id === liveActivity.parentInteractionId)
      : null;
    if (parent) return (parent.entityId && entityDataMap[parent.entityId]?.displayName) || parent.name || null;
    return currentSegment?.name ?? null;
    // currentSegment is declared above.
  }, [liveActivity, interactions, entityDataMap, currentSegment]);
  const onZone = useMemo(
    () => segmentZoneLookup(segments, trip.timezone ?? undefined),
    [segments, trip.timezone],
  );
  // Most recent activity by when it actually happened, not by when the record
  // was created or edited.
  const lastInteraction = useMemo(() => {
    if (interactions.length === 0) return null;
    const timeOf = (i: (typeof interactions)[number]) => activityEnd(i, onDay)?.getTime() ?? 0;
    return [...interactions].sort((a, b) => timeOf(b) - timeOf(a))[0] ?? null;
  }, [interactions, onDay]);

  // First segment starting on a later calendar day than today — today's
  // segments are "now", not "up next".
  const nextSegment = useMemo<SegmentWithId | null>(() => {
    const today = todayKey();
    return (
      segments
        .map((s) => ({ s, day: dayKeyOf(s.startDate) }))
        .filter((x): x is { s: SegmentWithId; day: string } => x.day != null && x.day > today)
        .sort((a, b) => (a.day < b.day ? -1 : a.day > b.day ? 1 : 0))[0]?.s ?? null
    );
  }, [segments]);

  const defaultSegmentId = useMemo(
    () => currentSegment?.id ?? findDefaultSegmentId(segments, interactions),
    [currentSegment, segments, interactions],
  );
  // The place you're plainly still inside, if there is one — tapping a nearby
  // ride from here files it under the park you checked into, the same as the
  // log sheet would. See `utils/activityTree`.
  const defaultParentId = useMemo(
    () =>
      findDefaultParentInteractionId(
        interactions,
        defaultSegmentId,
        (a) =>
          getInteractionEntityType(
            a.entityId ? { type: entityDataMap[a.entityId]?.type ?? undefined } : null,
            a.customResponses,
          ),
        onDay,
      ),
    [interactions, defaultSegmentId, entityDataMap, onDay],
  );
  // The hotel you're staying at, when you're out of it mid-stay.
  const { returnTo } = useHotelStays(interactions, entityDataMap, onDay);
  const [returning, setReturning] = useState(false);
  const handleBackAtHotel = async () => {
    if (!user || !returnTo || returning) return;
    setReturning(true);
    try {
      await startHotelVisit({
        userId: user.uid,
        tripId: trip.id,
        segmentId: defaultSegmentId,
        hotel: returnTo,
      });
    } finally {
      setReturning(false);
    }
  };

  const seedEntityIds = useMemo(
    () => segmentLogSeeds(segments, interactions, defaultSegmentId),
    [segments, interactions, defaultSegmentId],
  );

  const partyEntries = Object.entries(trip.travelParty || {});
  const segImg = currentSegment?.entityId
    ? entityDataMap[currentSegment.entityId]?.imageUrl ?? null
    : null;

  // Boarding-pass metrics, computed from real interactions/dates.
  const isLive = activeInteractions.length > 0;
  const tripDays = segmentDayCount(trip.startDate, trip.endDate);
  const loggedToday = useMemo(() => {
    const today = todayKey();
    return interactions.filter((i) => activityDayKey(i, onDay, onZone) === today).length;
  }, [interactions, onDay, onZone]);
  const dayLabel = useMemo(() => {
    const start = dayKeyOf(trip.startDate);
    if (!start) return status.label || null;
    const dayNum = daysBetweenKeys(start, todayKey()) + 1;
    if (dayNum < 1 || dayNum > tripDays) return status.label || null;
    return `Day ${dayNum} of ${tripDays}`;
  }, [trip.startDate, tripDays, status.label]);

  // A segment started from Today most likely sits inside whatever you're in
  // right now — a park day within the resort week. A cruise itinerary day is a
  // leaf, though, so a new segment there belongs alongside it under the cruise.
  const newSegmentParentId = useMemo(() => {
    if (!currentSegment) return null;
    return isCruiseDay(currentSegment) ? currentSegment.parentSegmentId ?? null : currentSegment.id;
  }, [currentSegment]);

  const handleCreateSegment = async (data: SegmentFormResult) => {
    const newId = await createSegment(trip.id, data);
    setSegmentOpen(false);
    navigate(`/trips/${trip.id}/segments/${newId}`);
  };

  const handleStartNearby = async (entityId: string, type: EntityType) => {
    if (!user || startingNearby) return;
    if (isContainerEntityType(type)) {
      navigate(
        `/entities/${entityId}${buildTripContextSearch(trip.id, defaultSegmentId, defaultParentId)}`,
      );
      return;
    }
    setStartingNearby(true);
    try {
      const id = await createInteractionAt({
        userId: user.uid,
        tripId: trip.id,
        segmentId: defaultSegmentId,
        parentInteractionId: defaultParentId,
        entityId,
        name: null,
      });
      navigate(`/trips/${trip.id}/interactions/${id}`);
    } catch {
      setStartingNearby(false);
    }
  };

  const tripActions = (
    <>
      {showFocusToggle && (
        <button
          type="button"
          className={`np-act${focused ? " on" : ""}`}
          onClick={onFocus}
          aria-pressed={focused}
          title={focused ? "Your main trip" : "Pin as your main trip"}
        >
          {focused ? "★" : "☆"}
        </button>
      )}
      <AddDropdown
        triggerClassName="np-act"
        items={[
          { label: "Add Segment", onClick: () => setSegmentOpen(true) },
          { label: "Log Activity", onClick: () => setLogOpen(true) },
        ]}
      />
    </>
  );

  const sheets = (
    <>
      <LogActivitySheet
        open={segmentOpen}
        onClose={() => setSegmentOpen(false)}
        title={`New segment · ${trip.name}`}
      >
        <SegmentForm
          onSubmit={handleCreateSegment}
          onCancel={() => setSegmentOpen(false)}
          segments={segments}
          defaultParentSegmentId={newSegmentParentId}
          defaultStartDate={new Date()}
          defaultEndDate={new Date()}
          travelParty={trip.travelParty || {}}
        />
      </LogActivitySheet>

      <LogActivitySheet
        open={logOpen}
        onClose={() => setLogOpen(false)}
        title={`Log activity · ${trip.name}`}
      >
        <LogActivityPicker
          tripId={trip.id}
          segments={segments}
          defaultSegmentId={defaultSegmentId}
          seedEntityIds={seedEntityIds}
          interactions={interactions}
        />
      </LogActivitySheet>
    </>
  );

  const backAtHotel = returnTo && (
    <BackAtHotelCard
      stay={returnTo}
      hotel={stayHotel(returnTo, entityDataMap)}
      tripId={trip.id}
      zone={trip.timezone ?? undefined}
      onBack={handleBackAtHotel}
      busy={returning}
    />
  );

  const quickRows = (list: typeof interactions) =>
    list.map((i) => (
      <QuickStepRow
        key={i.id}
        activity={i}
        entity={i.entityId ? entityDataMap[i.entityId] ?? null : null}
        type={typeOf(i)}
        tripId={trip.id}
        zone={trip.timezone ?? undefined}
      />
    ));

  const readySection = readyToStart.length > 0 && (
    <section className="quick-steps" aria-labelledby="ctc-ready-title">
      <h3 id="ctc-ready-title" className="ctc-section-title">Ready to start</h3>
      {quickRows(readyToStart)}
    </section>
  );

  const upNext = nextSegment && (
    <div className="ctc-next">
      Up next: <Link to={`/trips/${trip.id}/segments/${nextSegment.id}`}>{nextSegment.name}</Link>{" "}
      · {formatSegmentDateRange(nextSegment.startDate, nextSegment.endDate)}
    </div>
  );

  // Something is under way: it owns the screen, and the trip shrinks to a title.
  if (liveActivity) {
    return (
      <div className={`current-trip-card current-trip-card-live${focused ? " current-trip-card-focused" : ""}`}>
        <div className="ln-trip">
          <Link to={`/trips/${trip.id}`} className="ln-trip-text">
            <div className="ln-trip-eyebrow">
              {[dayLabel, currentSegment?.name].filter(Boolean).join(" · ")}
            </div>
            <h2 className="ln-trip-name">{trip.name}</h2>
          </Link>
          <div className="ln-trip-actions ln-trip-actions-light">{tripActions}</div>
        </div>

        <LiveNowCard
          activity={liveActivity}
          entity={liveActivity.entityId ? entityDataMap[liveActivity.entityId] ?? null : null}
          type={typeOf(liveActivity)}
          parentName={liveParentName}
          tripId={trip.id}
          zone={trip.timezone ?? undefined}
        />

        {backAtHotel}

        {readySection}

        {alsoOpen.length > 0 && (
          <section className="quick-steps" aria-labelledby="ctc-open-title">
            <h3 id="ctc-open-title" className="ctc-section-title">Also open</h3>
            {quickRows(alsoOpen)}
          </section>
        )}

        <div className="ctc-nearby">
          <NearbyEntities
            onPick={handleStartNearby}
            disabled={startingNearby}
            promptLabel="Up next nearby"
          />
        </div>

        <TodaySoFar
          interactions={interactions}
          exclude={shownAbove}
          entityDataMap={entityDataMap}
          typeOf={typeOf}
          onDay={onDay}
          onZone={onZone}
          tripId={trip.id}
          zone={trip.timezone ?? undefined}
        />

        {upNext}
        {sheets}
      </div>
    );
  }

  return (
    <div className={`current-trip-card${focused ? " current-trip-card-focused" : ""}`}>
      <div className="nowpass">
        <Link to={`/trips/${trip.id}`} className="np-tap">
          <div className="np-body">
            <div className="np-eyebrow">
              <span>{dayLabel}</span>
              {isLive && (
                <span className="statuspill live">
                  <span className="pulse" />
                  LIVE
                </span>
              )}
            </div>
            <h2>{trip.name}</h2>
            <div className="np-sub">
              {formatSegmentDateRange(trip.startDate, trip.endDate)}
              {currentSegment ? ` · ${currentSegment.name}` : ""}
            </div>
            {partyEntries.length > 0 && (
              <div className="np-party">
                {partyEntries.map(([key, member]) => (
                  <span key={key} className="np-av" title={member.displayName}>
                    {member.avatarUrl ? (
                      <img src={member.avatarUrl} alt={member.displayName} />
                    ) : (
                      member.displayName.charAt(0).toUpperCase()
                    )}
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="np-perf">
            <div className="p">
              <div className="k">Logged today</div>
              <div className="v">{loggedToday}</div>
            </div>
            <div className="p">
              <div className="k">Active now</div>
              <div className="v">{activeInteractions.length}</div>
            </div>
            <div className="p">
              <div className="k">{tripDays === 1 ? "Day" : "Days"}</div>
              <div className="v">{tripDays}</div>
            </div>
          </div>
        </Link>
        <div className="np-actions">{tripActions}</div>
      </div>

      {currentSegment && (
        <Link to={`/trips/${trip.id}/segments/${currentSegment.id}`} className="ctc-segment">
          <span className="ctc-segment-icon">
            {segImg ? (
              <img src={segImg} alt="" />
            ) : (
              <TypeIcon
                type={currentSegment.entityId ? entityDataMap[currentSegment.entityId]?.type : null}
                tile
              />
            )}
          </span>
          <span className="ctc-segment-text">
            <span className="ctc-segment-name">
              {currentSegment.name}
              <span className="segment-now-pill">NOW</span>
            </span>
            <span className="ctc-segment-dates">
              {formatSegmentDateRange(currentSegment.startDate, currentSegment.endDate)}
            </span>
          </span>
        </Link>
      )}

      <div className="ctc-activity">
        {backAtHotel}
        {readySection}
        {interactionsLoading ? (
          <div className="loading">Loading…</div>
        ) : activeInteractions.length > 0 ? (
          <section className="quick-steps" aria-labelledby="ctc-now-title">
            <h3 id="ctc-now-title" className="ctc-section-title">Happening now</h3>
            {quickRows(activeInteractions)}
          </section>
        ) : readyToStart.length > 0 ? null : lastInteraction ? (
          <>
            <h3 className="ctc-section-title">Last activity</h3>
            <ActivityList
              onDay={onDay}
              activities={[lastInteraction]}
              entityMap={entityMap}
              entityDataMap={entityDataMap}
              tripId={trip.id}
            />
          </>
        ) : (
          <p className="text-muted ctc-empty">
            Nothing logged yet — tap <strong>+</strong> to start something.
          </p>
        )}
        <div className="ctc-nearby">
          <NearbyEntities
            onPick={handleStartNearby}
            disabled={startingNearby}
            promptLabel="Start at a place near you"
          />
        </div>
      </div>

      {upNext}
      {sheets}
    </div>
  );
}
